/**
 * @fileoverview Progress Photos Section Component
 *
 * Extracted from AdminProjectDetail.js and refactored for AdminProjectDetails.js
 *
 * @description Displays and manages progress photos for a project with multiple
 * view modes (calendar, list, gallery), filtering, and CRUD operations.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires @tanstack/react-query
 */

import React, { useMemo, useState } from "react";
import {
  FaCamera,
  FaCalendarAlt,
  FaCalendarWeek,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaTrash,
  FaFilter,
  FaList,
  FaLayerGroup,
  FaPlus,
  FaTh,
  FaTimes,
  FaDownload,
  FaCircle,
} from "react-icons/fa";

import "../AdminProjectDetails.css";
import "./ProgressPhotos.css";

import { formatDateET } from "../../../../utils/dateUtils";
import { useProgressPhotos } from "../../../../hooks/useUpdates";

import ProgressPhotoUploadModal from "../../../../components/admin/ProgressPhotoUploadModal";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { functions } from "../../../../firebase";
import { httpsCallable } from "firebase/functions";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Available photo view modes
 * @constant {Array<Object>}
 */
const PHOTO_VIEW_MODES = [
  { value: "calendar", label: "Calendar", icon: FaCalendarAlt },
  { value: "list", label: "List", icon: FaList },
  { value: "gallery", label: "Gallery", icon: FaTh },
];

/**
 * List grouping options
 * @constant {Array<Object>}
 */
const PHOTO_LIST_GROUPING = [
  { value: "week", label: "By Week", icon: FaCalendarWeek },
  { value: "phase", label: "By Phase", icon: FaLayerGroup },
];

/** Number of week/phase groups per page in list view */
const LIST_GROUPS_PER_PAGE = 5;

/** Number of photos per page in gallery view (3 rows × 4 columns) */
const GALLERY_PHOTOS_PER_PAGE = 12;

/* ============================================
   HELPERS
   ============================================ */

/**
 * Get calendar days for a given month
 * @param {number} year - The year
 * @param {number} month - The month (0-indexed)
 * @returns {Array<number|null>} Array of day numbers with null padding
 */
const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const days = [];
  for (let i = 0; i < startingDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  return days;
};

/**
 * Format a week key into a readable date range
 * @param {string} weekKey - ISO date string for start of week
 * @returns {string} Formatted date range string
 */
const formatWeekLabel = (weekKey) => {
  const start = new Date(weekKey + "T12:00:00");
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const options = { month: "short", day: "numeric" };

  return `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString(
    "en-US",
    options,
  )}`;
};

/**
 * Transform photo object from useProgressPhotos format to ProgressPhotoUploadModal format
 * Maps field names from the hook data structure to modal expected structure
 *
 * IMPORTANT: The useProgressPhotos hook returns photos with TWO different ID fields:
 * - `id` = Airtable attachment ID (from the attachment object itself)
 * - `recordId` = Airtable record ID (the Files & Photos table record ID)
 *
 * For update/delete operations, we need the `recordId`, not the attachment `id`.
 *
 * @param {Object} photo - Photo object from useProgressPhotos hook
 * @param {string} photo.recordId - Airtable record ID (for CRUD operations)
 * @param {string} photo.id - Attachment ID (not used for updates)
 * @param {string} photo.name - Photo display name
 * @param {string} photo.url - Photo URL
 * @param {string} photo.constructionPhase - Construction phase name
 * @param {string} photo.uploadDate - Upload date string
 * @param {string} [photo.notes] - Optional notes
 * @param {string} [photo.projectId] - Optional project ID
 * @param {string} projectId - Current project ID (fallback if not in photo)
 * @returns {Object} Transformed photo object for modal
 */
const transformPhotoForModal = (photo, projectId) => {
  if (!photo) return null;

  // Use recordId (Airtable record ID) for CRUD operations, fall back to id if recordId not present
  const airtableRecordId = photo.recordId || photo.id;

  return {
    id: airtableRecordId,
    attachmentId: photo.id,
    fileName: photo.name || "",
    phase: photo.constructionPhase || "",
    notes: photo.notes || "",
    projectId: photo.projectId || projectId || "",
    taskId:
      photo.taskId || photo.relatedTaskId || photo.relatedTaskIds?.[0] || "",
    uploadDate: photo.uploadDate || null,
    // Transform URL to attachments array format expected by modal
    attachments: photo.url
      ? [{ url: photo.url, filename: photo.name || "photo" }]
      : [],
    // Keep original fields for reference
    _originalUrl: photo.url,
    _attachmentId: photo.id, // Keep original attachment ID for reference
  };
};

/* ============================================
   SMALL SHARED WRAPPER
   ============================================ */

/**
 * Section wrapper component with header and action slot
 * @param {Object} props - Component props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.icon - Optional icon element
 * @param {React.ReactNode} props.action - Optional action button/element
 * @param {React.ReactNode} props.children - Section content
 * @returns {JSX.Element} Section component
 */
const Section = ({ title, icon, action, children }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/* ============================================
   UI SUBCOMPONENTS
   ============================================ */

/**
 * Photo view mode toggle buttons
 * @param {Object} props - Component props
 * @param {string} props.activeMode - Currently active view mode
 * @param {Function} props.onChange - Callback when mode changes
 * @returns {JSX.Element} Toggle component
 */
const PhotoViewToggle = ({ activeMode, onChange }) => (
  <div className="apd-photo-view-toggle">
    {PHOTO_VIEW_MODES.map((mode) => {
      const Icon = mode.icon;
      return (
        <button
          key={mode.value}
          className={`apd-photo-view-btn ${activeMode === mode.value ? "active" : ""}`}
          onClick={() => onChange(mode.value)}
          data-tooltip={mode.label}
          type="button"
        >
          <Icon />
          <span className="apd-photo-view-label">{mode.label}</span>
        </button>
      );
    })}
  </div>
);

/**
 * Calendar view for photos
 * @param {Object} props - Component props
 * @param {Date} props.month - Current month being displayed
 * @param {Object} props.photosByDate - Photos grouped by date
 * @param {Function} props.onDayClick - Handler for day click
 * @param {Function} props.onPrevMonth - Handler for previous month
 * @param {Function} props.onNextMonth - Handler for next month
 * @returns {JSX.Element} Calendar view component
 */
const PhotoCalendarView = ({
  month,
  photosByDate,
  onDayClick,
  onPrevMonth,
  onNextMonth,
}) => {
  const days = getCalendarDays(month.getFullYear(), month.getMonth());
  const monthName = month.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="photos-calendar">
      <div className="photos-calendar-header">
        <button
          className="photos-calendar-nav"
          onClick={onPrevMonth}
          type="button"
        >
          <FaChevronLeft />
        </button>
        <h3 className="photos-calendar-title">{monthName}</h3>
        <button
          className="photos-calendar-nav"
          onClick={onNextMonth}
          type="button"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="photos-calendar-weekdays">
        {weekdays.map((day) => (
          <div key={day} className="photos-calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="photos-calendar-grid">
        {days.map((day, index) => {
          const dateKey = day
            ? `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(
                2,
                "0",
              )}-${String(day).padStart(2, "0")}`
            : null;

          const hasPhotos = dateKey && photosByDate[dateKey];
          const photoCount = hasPhotos ? photosByDate[dateKey].length : 0;

          return (
            <div
              key={index}
              className={[
                "photos-calendar-day",
                !day ? "is-empty" : "",
                hasPhotos ? "has-photos" : "",
              ].join(" ")}
              onClick={() => onDayClick(day)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") onDayClick(day);
              }}
            >
              {day && (
                <>
                  <span className="photos-calendar-day-num">{day}</span>
                  {hasPhotos && (
                    <span className="photos-calendar-photo-indicator">
                      <FaCamera />
                      <span>{photoCount}</span>
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * List view for photos with grouping
 * @param {Object} props - Component props
 * @param {string} props.grouping - Grouping type ('week' or 'phase')
 * @param {Object} props.photosByWeek - Photos grouped by week
 * @param {Object} props.photosByPhase - Photos grouped by phase
 * @param {Object} props.expandedGroups - Map of expanded group keys
 * @param {Function} props.onToggleGroup - Handler for group toggle
 * @param {Function} props.onPhotoClick - Handler for photo click
 * @param {Function} props.onEditPhoto - Handler for edit action
 * @param {Function} props.onDeletePhoto - Handler for delete action
 * @returns {JSX.Element} List view component
 */
/**
 * Pagination controls
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="apd-pagination">
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
    >
      <FaChevronLeft />
    </button>
    <span className="apd-pagination-info">
      Page {currentPage} of {totalPages}
    </span>
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
    >
      <FaChevronRight />
    </button>
  </div>
);

const PhotoListView = ({
  grouping,
  photosByWeek,
  photosByPhase,
  expandedGroups,
  onToggleGroup,
  onPhotoClick,
  onEditPhoto,
  onDeletePhoto,
  currentPage,
  onPageChange,
}) => {
  const groups = grouping === "week" ? photosByWeek : photosByPhase;

  const sortedKeys = Object.keys(groups).sort((a, b) =>
    grouping === "week" ? b.localeCompare(a) : a.localeCompare(b),
  );

  if (sortedKeys.length === 0) {
    return <p className="apd-empty-message">No photos found</p>;
  }

  const totalPages = Math.ceil(sortedKeys.length / LIST_GROUPS_PER_PAGE);
  const startIndex = (currentPage - 1) * LIST_GROUPS_PER_PAGE;
  const paginatedKeys = sortedKeys.slice(
    startIndex,
    startIndex + LIST_GROUPS_PER_PAGE,
  );

  return (
    <div className="photos-list">
      {paginatedKeys.map((groupKey) => {
        const groupPhotos = groups[groupKey];
        const isExpanded = !!expandedGroups[groupKey];
        const label =
          grouping === "week" ? formatWeekLabel(groupKey) : groupKey;

        return (
          <div key={groupKey} className="photos-list-group">
            <button
              className="photos-list-header"
              onClick={() => onToggleGroup(groupKey)}
              type="button"
            >
              <div className="photos-list-header-left">
                {grouping === "week" ? <FaCalendarWeek /> : <FaLayerGroup />}
                <span className="photos-list-header-label">{label}</span>
              </div>
              <div className="photos-list-header-right">
                <span className="photos-list-count">
                  {groupPhotos.length} photos
                </span>
                <span
                  className={`photos-list-chevron ${isExpanded ? "expanded" : ""}`}
                >
                  <FaChevronDown />
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="photos-list-content">
                {groupPhotos.map((photo, idx) => (
                  <div key={photo.id} className="photos-list-item-wrapper">
                    <div
                      className="photos-list-item"
                      onClick={() => onPhotoClick(groupPhotos, idx)}
                      type="button"
                    >
                      <img
                        src={photo.url}
                        alt={photo.name}
                        className="photos-list-thumb"
                        loading="lazy"
                      />
                      <div className="photos-list-item-info">
                        <span className="photos-list-item-name">
                          {photo.name}
                        </span>
                        <span className="photos-list-item-date">
                          {formatDateET(photo.uploadDate)}
                        </span>
                      </div>
                    </div>
                    {/* Hover Actions */}
                    <div className="photos-list-item-actions">
                      <button
                        className="photos-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPhoto(photo);
                        }}
                        data-tooltip="Edit photo"
                        type="button"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="photos-action-btn photos-action-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeletePhoto(photo);
                        }}
                        data-tooltip="Delete photo"
                        type="button"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

/**
 * Gallery view for photos
 * @param {Object} props - Component props
 * @param {Array} props.photos - Array of photo objects
 * @param {Function} props.onPhotoClick - Handler for photo click
 * @param {Function} props.onEditPhoto - Handler for edit action
 * @param {Function} props.onDeletePhoto - Handler for delete action
 * @returns {JSX.Element} Gallery view component
 */
const PhotoGalleryView = ({
  photos,
  onPhotoClick,
  onEditPhoto,
  onDeletePhoto,
  currentPage,
  onPageChange,
}) => {
  if (!photos.length)
    return <p className="apd-empty-message">No photos found</p>;

  const totalPages = Math.ceil(photos.length / GALLERY_PHOTOS_PER_PAGE);
  const startIndex = (currentPage - 1) * GALLERY_PHOTOS_PER_PAGE;
  const paginatedPhotos = photos.slice(
    startIndex,
    startIndex + GALLERY_PHOTOS_PER_PAGE,
  );

  return (
    <>
      <div className="photos-gallery">
        {paginatedPhotos.map((photo, idx) => (
          <div key={photo.id} className="photos-gallery-item">
            <button
              className="photos-gallery-thumb"
              onClick={() => onPhotoClick(photos, startIndex + idx)}
              type="button"
              photo-tooltip={`${photo.name} - ${formatDateET(photo.uploadDate)}`}
            >
              <img src={photo.url} alt={photo.name} loading="lazy" />
            </button>
            {/* Hover Actions */}
            <div className="photos-gallery-actions">
              <button
                className="photos-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditPhoto(photo);
                }}
                data-tooltip="Edit photo"
                type="button"
              >
                <FaEdit />
              </button>
              <button
                className="photos-action-btn photos-action-btn-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePhoto(photo);
                }}
                data-tooltip="Delete photo"
                type="button"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

/**
 * Modal showing photos for a specific day (from calendar view)
 * @param {Object} props - Component props
 * @param {Object} props.data - Day data with date and photos
 * @param {Function} props.onClose - Handler for closing modal
 * @param {Function} props.onPhotoClick - Handler for photo click
 * @returns {JSX.Element|null} Day photos modal or null
 */
const DayPhotosModal = ({ data, onClose, onPhotoClick }) => {
  if (!data) return null;

  const dateLabel = new Date(data.date + "T12:00:00").toLocaleDateString(
    "en-US",
    {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    },
  );

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="photos-day-modal" onClick={(e) => e.stopPropagation()}>
        <div className="apd-modal-header">
          <h3>{dateLabel}</h3>
          <button
            className="apd-modal-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <div className="photos-day-grid">
          {data.photos.map((photo, idx) => (
            <button
              key={photo.id}
              className="photos-day-card"
              onClick={() => onPhotoClick(data.photos, idx)}
              type="button"
            >
              <img src={photo.url} alt={photo.name} loading="lazy" />
              <div className="photos-day-info">
                <span className="photos-day-name">{photo.name}</span>
                <span className="photos-day-phase">
                  {photo.constructionPhase}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Full-screen lightbox for viewing photos
 * @param {Object} props - Component props
 * @param {Object} props.data - Lightbox state with photos and current index
 * @param {Function} props.onClose - Handler for closing lightbox
 * @param {Function} props.onNavigate - Handler for navigation
 * @param {Function} props.onEdit - Handler for edit action
 * @param {Function} props.onDelete - Handler for delete action
 * @param {Function} props.onDownload - Handler for download action
 * @returns {JSX.Element|null} Lightbox component or null
 */
const PhotoLightbox = ({
  data,
  onClose,
  onNavigate,
  onEdit,
  onDelete,
  onDownload,
}) => {
  if (!data?.isOpen || !data.photos?.length) return null;
  const photo = data.photos[data.currentIndex];

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="photos-lightbox" onClick={(e) => e.stopPropagation()}>
        <button
          className="photos-lightbox-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <FaTimes />
        </button>

        {data.photos.length > 1 && (
          <>
            <button
              className="photos-lightbox-nav prev"
              onClick={() => onNavigate("prev")}
              type="button"
              aria-label="Previous photo"
            >
              <FaChevronLeft />
            </button>
            <button
              className="photos-lightbox-nav next"
              onClick={() => onNavigate("next")}
              type="button"
              aria-label="Next photo"
            >
              <FaChevronRight />
            </button>
          </>
        )}

        <img
          src={photo.url}
          alt={photo.name}
          className="photos-lightbox-image"
        />

        <div className="photos-lightbox-info">
          {/* Action Buttons */}
          <div className="photos-lightbox-actions">
            <button
              className="photos-lightbox-action-btn"
              onClick={() => onDownload(photo)}
              type="button"
            >
              <FaDownload /> Download
            </button>
            <button
              className="photos-lightbox-action-btn"
              onClick={() => onEdit(photo)}
              type="button"
            >
              <FaEdit /> Edit
            </button>
            <button
              className="photos-lightbox-action-btn photos-lightbox-delete-btn"
              onClick={() => onDelete(photo)}
              type="button"
            >
              <FaTrash /> Delete
            </button>
          </div>
          <div className="photos-lightbox-title-wrapper">
            <div className="photos-lightbox-title">{photo.name}</div>
            <div className="photos-lightbox-phase">
              {photo.constructionPhase}
            </div>
          </div>
          <div className="photos-lightbox-meta">
            <span className="photos-lightbox-date">
              {formatDateET(photo.uploadDate)}
            </span>
            {data.photos.length > 1 && (
              <span className="photos-lightbox-counter">
                <FaCircle /> {data.currentIndex + 1} of {data.photos.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * Progress Photos section component for Admin Project Details
 * Displays project progress photos with multiple view modes, filtering,
 * and CRUD operations.
 *
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project record ID
 * @param {Object} props.project - Project data object
 * @returns {JSX.Element} Progress photos section
 */
const ProgressPhotos = ({ projectId, project }) => {
  const queryClient = useQueryClient();

  // Pull fresh photo data inside this section component
  const { data: photosData } = useProgressPhotos(projectId);

  // Normalize photo array shape (matches original AdminProjectDetail.js behavior)
  const photos = useMemo(() => {
    return photosData?.data?.photos || photosData?.photos || photosData || [];
  }, [photosData]);

  // State - Modals & Controls
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [photoToEdit, setPhotoToEdit] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [photoViewMode, setPhotoViewMode] = useState("list");
  const [photoPhaseFilter, setPhotoPhaseFilter] = useState("all");
  const [photoListGrouping, setPhotoListGrouping] = useState("week");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [expandedPhotoGroups, setExpandedPhotoGroups] = useState({});
  const [listPage, setListPage] = useState(1);
  const [galleryPage, setGalleryPage] = useState(1);
  const [selectedDayPhotos, setSelectedDayPhotos] = useState(null);
  const [photoLightbox, setPhotoLightbox] = useState({
    isOpen: false,
    photos: [],
    currentIndex: 0,
  });

  // Unique phases from photos (use constructionPhase field)
  const photoPhases = useMemo(() => {
    return [...new Set(photos.map((p) => p.constructionPhase).filter(Boolean))];
  }, [photos]);

  // Filter photos by phase (constructionPhase)
  const filteredPhotos = useMemo(() => {
    if (photoPhaseFilter === "all") return photos;
    return photos.filter((p) => p.constructionPhase === photoPhaseFilter);
  }, [photos, photoPhaseFilter]);

  // Group photos by date for calendar
  const photosByDate = useMemo(() => {
    return filteredPhotos.reduce((acc, photo) => {
      const dateKey = photo.uploadDate || "unknown";
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(photo);
      return acc;
    }, {});
  }, [filteredPhotos]);

  /**
   * Get week key from date string
   * @param {string} dateStr - Date string in YYYY-MM-DD format
   * @returns {string} Week key (start of week date)
   */
  const getWeekKey = (dateStr) => {
    if (!dateStr) return "unknown";
    const date = new Date(dateStr + "T12:00:00");
    if (Number.isNaN(date.getTime())) return "unknown";
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    return startOfWeek.toISOString().split("T")[0];
  };

  // Group photos by week
  const photosByWeek = useMemo(() => {
    return filteredPhotos.reduce((acc, photo) => {
      const weekKey = getWeekKey(photo.uploadDate);
      if (!acc[weekKey]) acc[weekKey] = [];
      acc[weekKey].push(photo);
      return acc;
    }, {});
  }, [filteredPhotos]);

  // Group photos by construction phase (display grouping)
  const photosByPhase = useMemo(() => {
    return filteredPhotos.reduce((acc, photo) => {
      const phase = photo.constructionPhase || "Unassigned";
      if (!acc[phase]) acc[phase] = [];
      acc[phase].push(photo);
      return acc;
    }, {});
  }, [filteredPhotos]);

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: async ({ recordId, attachmentId }) => {
      const deleteFn = httpsCallable(functions, "deleteFilesPhotosAttachment");
      const result = await deleteFn({ recordId, attachmentId });

      if (!result.data?.success) {
        throw new Error(result.data?.error || "Failed to delete photo");
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["progressPhotos", projectId]);
      setShowDeleteConfirmation(false);
      setPhotoToDelete(null);

      // If viewer is open and showing deleted photo, close it
      // Compare using recordId since that's the Airtable record ID
      if (photoLightbox.isOpen && photoToDelete) {
        const currentPhoto = photoLightbox.photos[photoLightbox.currentIndex];
        const currentRecordId = currentPhoto?.recordId || currentPhoto?.id;
        const deletedRecordId = photoToDelete?.recordId || photoToDelete?.id;
        if (currentRecordId === deletedRecordId) {
          closeLightbox();
        }
      }
    },
    onError: (error) => {
      console.error("Delete photo error:", error);
      alert(`Failed to delete photo: ${error.message}`);
    },
  });

  /* ============================================
     HANDLERS
     ============================================ */

  const openUploadModal = () => setShowUploadModal(true);
  const closeUploadModal = () => setShowUploadModal(false);

  const handleUploadComplete = () => {
    queryClient.invalidateQueries(["progressPhotos", projectId]);
    closeUploadModal();
  };

  /**
   * Handle edit photo action - transforms photo data for modal
   * @param {Object} photo - Photo object from the photos array
   */
  const handleEditPhoto = (photo) => {
    // Transform the photo object to match what ProgressPhotoUploadModal expects
    const transformedPhoto = transformPhotoForModal(photo, projectId);
    setPhotoToEdit(transformedPhoto);
    setShowEditModal(true);
    // Close lightbox if open
    if (photoLightbox.isOpen) {
      closeLightbox();
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setPhotoToEdit(null);
  };

  const handleEditComplete = () => {
    queryClient.invalidateQueries(["progressPhotos", projectId]);
    closeEditModal();
  };

  const handleDeleteRequest = (photo) => {
    setPhotoToDelete(photo);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (photoToDelete) {
      deletePhotoMutation.mutate({
        recordId: photoToDelete.recordId || photoToDelete.id,
        attachmentId: photoToDelete.id,
      });
    }
  };

  /**
   * Handle photo download
   * @param {Object} photo - Photo object to download
   */
  const handleDownloadPhoto = (photo) => {
    if (photo?.url) {
      const link = document.createElement("a");
      link.href = photo.url;
      link.download = `${photo.name || "photo"}.jpg`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrevMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1),
    );
  };

  /**
   * Handle calendar day click
   * @param {number|null} day - Day number clicked
   */
  const handleDayClick = (day) => {
    if (!day) return;
    const dateKey = `${calendarMonth.getFullYear()}-${String(
      calendarMonth.getMonth() + 1,
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    const dayPhotos = photosByDate[dateKey];
    if (dayPhotos?.length)
      setSelectedDayPhotos({ date: dateKey, photos: dayPhotos });
  };

  /**
   * Toggle photo group expansion
   * @param {string} groupKey - Group key to toggle
   */
  const togglePhotoGroup = (groupKey) => {
    setExpandedPhotoGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  /**
   * Open photo lightbox
   * @param {Array} photosArray - Array of photos to display
   * @param {number} startIndex - Starting index
   */
  const openPhotoLightbox = (photosArray, startIndex = 0) => {
    setPhotoLightbox({
      isOpen: true,
      photos: photosArray,
      currentIndex: startIndex,
    });
  };

  const closeLightbox = () => {
    setPhotoLightbox({ isOpen: false, photos: [], currentIndex: 0 });
  };

  /**
   * Navigate within lightbox
   * @param {string} direction - 'next' or 'prev'
   */
  const navigateLightbox = (direction) => {
    setPhotoLightbox((prev) => ({
      ...prev,
      currentIndex:
        direction === "next"
          ? (prev.currentIndex + 1) % prev.photos.length
          : (prev.currentIndex - 1 + prev.photos.length) % prev.photos.length,
    }));
  };

  /* ============================================
     RENDER
     ============================================ */

  return (
    <>
      <Section
        title="Progress Photos"
        icon={<FaCamera />}
        action={
          <button
            className="apd-add-btn"
            onClick={openUploadModal}
            type="button"
          >
            <FaPlus /> <span className="hide-sm">Add Photos</span>
          </button>
        }
      >
        {/* Controls Row (reuses apd-* styles from AdminProjectDetails.css) */}
        <div className="apd-photo-controls">
          <PhotoViewToggle
            activeMode={photoViewMode}
            onChange={setPhotoViewMode}
          />{" "}
          <div className="apd-photo-filter">
            {/* List Grouping */}
            {photoViewMode === "list" && (
              <div className="apd-photo-grouping">
                {PHOTO_LIST_GROUPING.map((opt) => (
                  <button
                    key={opt.value}
                    className={`apd-grouping-btn ${
                      photoListGrouping === opt.value ? "active" : ""
                    }`}
                    onClick={() => {
                      setPhotoListGrouping(opt.value);
                      setListPage(1);
                    }}
                    type="button"
                  >
                    <span className="apd-grouping-label">
                      {opt.icon && <opt.icon />}
                      <span className="hide-sm">{opt.label}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="apd-photo-filters">
            {/* Phase Filter */}
            <div className="apd-photo-filter">
              <FaFilter />
              <select
                value={photoPhaseFilter}
                onChange={(e) => {
                  setPhotoPhaseFilter(e.target.value);
                  setListPage(1);
                  setGalleryPage(1);
                }}
              >
                <option value="all">All Phases</option>
                {photoPhases.map((phase) => (
                  <option key={phase} value={phase}>
                    {phase}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <p className="apd-photo-total">{filteredPhotos.length} photos</p>

        <div className="photos-view-content">
          {photoViewMode === "calendar" && (
            <PhotoCalendarView
              month={calendarMonth}
              photosByDate={photosByDate}
              onDayClick={handleDayClick}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
            />
          )}

          {photoViewMode === "list" && (
            <PhotoListView
              grouping={photoListGrouping}
              photosByWeek={photosByWeek}
              photosByPhase={photosByPhase}
              expandedGroups={expandedPhotoGroups}
              onToggleGroup={togglePhotoGroup}
              onPhotoClick={openPhotoLightbox}
              onEditPhoto={handleEditPhoto}
              onDeletePhoto={handleDeleteRequest}
              currentPage={listPage}
              onPageChange={setListPage}
            />
          )}

          {photoViewMode === "gallery" && (
            <PhotoGalleryView
              photos={filteredPhotos}
              onPhotoClick={openPhotoLightbox}
              onEditPhoto={handleEditPhoto}
              onDeletePhoto={handleDeleteRequest}
              currentPage={galleryPage}
              onPageChange={setGalleryPage}
            />
          )}
        </div>
      </Section>

      {/* Day Photos Modal (Calendar) */}
      {selectedDayPhotos && (
        <DayPhotosModal
          data={selectedDayPhotos}
          onClose={() => setSelectedDayPhotos(null)}
          onPhotoClick={(arr, idx) => {
            setSelectedDayPhotos(null);
            openPhotoLightbox(arr, idx);
          }}
        />
      )}

      {/* Lightbox */}
      <PhotoLightbox
        data={photoLightbox}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
        onEdit={handleEditPhoto}
        onDelete={handleDeleteRequest}
        onDownload={handleDownloadPhoto}
      />

      {/* Upload Modal */}
      <ProgressPhotoUploadModal
        isOpen={showUploadModal}
        onClose={closeUploadModal}
        mode="create"
        projectId={projectId}
        projectName={project?.projectName}
        project={project}
        onUploadComplete={handleUploadComplete}
      />

      {/* Edit Modal */}
      {photoToEdit && (
        <ProgressPhotoUploadModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          mode="edit"
          existingPhoto={photoToEdit}
          projectId={projectId}
          projectName={project?.projectName}
          project={project}
          onUploadComplete={handleEditComplete}
          onDelete={() => {
            // Transform back to original format for delete
            const originalPhoto = {
              id: photoToEdit.id,
              name: photoToEdit.fileName,
            };
            handleDeleteRequest(originalPhoto);
            closeEditModal();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setPhotoToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Progress Photo?"
        message={`Are you sure you want to delete progress photo: `}
        item={photoToDelete?.name || photoToDelete?.fileName || "untitled"}
        punc="? "
        warning={`This will permanently delete this photo for all users, including clients.`}
        proceed="This action cannot be undone."
        confirmText="Delete"
        isDestructive={true}
        isLoading={deletePhotoMutation.isPending}
      />
    </>
  );
};

export default ProgressPhotos;
